/**
 * Offer-in-Compromise workflow types (milestone 03-14). The OIC flow is a thin
 * guided orchestration over the Tier 2.2 worksheet offer calc + Tier 3 forms —
 * it owns only progress + terms + gate state; every dollar figure it shows is
 * read live from the backend summary (integer cents).
 */

export type OICStatus =
  | "NOT_STARTED"
  | "IN_PROGRESS"
  | "INELIGIBLE_CSED"
  | "PACKET_READY"
  | "SUBMITTED"
  | "WITHDRAWN";

export type OICStep = "financials" | "rcp" | "eligibility" | "terms" | "packet";

export type OfferPlan = 12 | 24;

export type OICBasis = "" | "DATC" | "ETA_HARDSHIP" | "ETA_PUBLIC_POLICY";

export interface OICFlow {
  id: string;
  case: string;
  status: OICStatus;
  statusDisplay: string;
  step: OICStep;
  stepDisplay: string;
  offerPlan: OfferPlan | null;
  offerAmountCents: number | null;
  basis: OICBasis;
  basisDisplay: string;
  calcAssetsCents: number | null;
  calcRmiCents: number | null;
  calcTaxDueCents: number | null;
  csedMonths: number | null;
  iaType: string;
  fullPayPossible: boolean | null;
  eligibilityAck: boolean;
  eligibilityNote: string;
  fullPayOverride: boolean;
  submittedOn: string | null;
  createdAt: string;
  updatedAt: string;
}

/** The assembled RCP / Summary-of-Alternatives + eligibility gates (Step 2/3). */
export interface OICSummary {
  available: boolean;
  rmiCents?: number;
  totalAssetsCents?: number;
  taxDueCents?: number;
  csedMonths?: number;
  lumpSumOfferCents?: number;
  periodicOfferCents?: number;
  fullPayPossible?: boolean;
  iaType?: string;
  iaTypeLabel?: string;
  iaCondition?: string;
  iaMonthlyPaymentCents?: number;
  iaCalculatedMonths?: number;
  interestRate?: string;
  cncLikely?: boolean;
  missingCseds?: number[];
  expiredCseds?: number[];
  expired?: boolean;
  zeroDue?: boolean;
  warnings: string[];
}

export interface PacketFormResult {
  formNumber: string;
  documentId: string | null;
  status: "generated" | "pending";
}

export interface PacketResult {
  forms: PacketFormResult[];
  pending: string[];
}

export interface StepDef {
  key: OICStep;
  label: string;
  short: string;
}

export const OIC_STEPS: StepDef[] = [
  { key: "financials", label: "Financials", short: "Financials" },
  { key: "rcp", label: "Summary of Alternatives", short: "RCP" },
  { key: "eligibility", label: "Eligibility", short: "Eligibility" },
  { key: "terms", label: "Terms", short: "Terms" },
  { key: "packet", label: "Packet", short: "Packet" },
];

export const oicStepIndex = (step: OICStep): number =>
  Math.max(0, OIC_STEPS.findIndex((s) => s.key === step));

/** The offer amount computed for a plan, from the summary (min $1). */
export const offerForPlan = (summary: OICSummary, plan: OfferPlan): number | undefined =>
  plan === 24 ? summary.periodicOfferCents : summary.lumpSumOfferCents;
